import { useEffect } from 'react';
import { useCanvasStore } from '../store/canvas-store';
import { isPanelOwned, isToolFrame, type Frame } from '../store/types';
import { historyStore } from './historyStore';

const KEEP_PER_FRAME = 30;
const DEBOUNCE_MS = 1500;

/** Content fingerprint of a frame, ignoring geometry/selection. */
export function contentKey(f: Frame): string {
  const anyF = f as unknown as Record<string, unknown>;
  // Keep in sync when adding content-bearing fields to design-frame interfaces.
  const pick = ['kind', 'html', 'tsx', 'slots', 'slotValues', 'propValues', 'propsSchema', 'locked', 'script', 'motion', 'effect', 'themeId', 'title', 'mainId'];
  return JSON.stringify(pick.map((k) => anyF[k] ?? null));
}

/** Design frames whose content changed between two frame arrays (tool/panel frames skipped). */
export function diffContentChanges(prev: Frame[], next: Frame[]): Frame[] {
  const prevById = new Map(prev.map((f) => [f.id, f] as const));
  return next.filter((f) => {
    // Skip tool frames (chat/code/fx/note/styleguide) — note frames carry an
    // `html` field but are still tool frames. Panel-owned frames are always
    // tool frames, but keep the guard explicit for safety.
    if (isPanelOwned(f) || isToolFrame(f)) return false;
    const before = prevById.get(f.id);
    return !before || contentKey(before) !== contentKey(f);
  });
}

/** App-root hook: debounced per-frame version capture into IndexedDB. */
export function useFrameVersioning(): void {
  useEffect(() => {
    const timers = new Map<string, ReturnType<typeof setTimeout>>();
    let prevFrames = useCanvasStore.getState().doc.frames;
    const unsub = useCanvasStore.subscribe((s) => {
      const next = s.doc.frames;
      if (next === prevFrames) return;
      const changed = diffContentChanges(prevFrames, next);
      // GC versions for frames that have been removed from the canvas.
      const nextIds = new Set(next.map((f) => f.id));
      for (const f of prevFrames) {
        if (!nextIds.has(f.id)) {
          const t = timers.get(f.id);
          if (t) { clearTimeout(t); timers.delete(f.id); }
          void historyStore.deleteFrameVersions(f.id).catch(() => {});
        }
      }
      prevFrames = next;
      for (const f of changed) {
        const existing = timers.get(f.id);
        if (existing) clearTimeout(existing);
        // Each reset re-captures the latest `f` (settled-state snapshot): the
        // debounce fires once edits stop, recording the final frame — not a
        // stale-closure bug.
        timers.set(f.id, setTimeout(() => {
          timers.delete(f.id);
          void historyStore.putFrameVersion(f.id, f)
            .then(() => historyStore.pruneFrameVersions(f.id, KEEP_PER_FRAME))
            .catch(() => {});
        }, DEBOUNCE_MS));
      }
    });
    return () => { for (const t of timers.values()) clearTimeout(t); unsub(); };
  }, []);
}
